import { useEffect, useState } from "react";
import {
  Folder,
  LayoutGrid,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
} from "@digital-twins/design-system/icons";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type {
  LibraryNavigationRailProps,
  LibraryNavigationRow,
} from "@/types/components/library-navigation";

function readCollapsed(key: string) {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

/** Presentation only: callers supply authorized rows, counts and domain actions. */
export function LibraryNavigationRail({
  title,
  allLabel,
  newLabel,
  collapseLabel,
  expandLabel,
  storageKey,
  groups,
  activeToken,
  isAllActive,
  onSelect,
  onSelectAll,
  onNew,
  newDisabled,
  newHint,
  isLoading,
  hasError,
  errorLabel,
  testIdPrefix: id,
  filters,
  className,
}: LibraryNavigationRailProps) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey));
  useEffect(() => setCollapsed(readCollapsed(storageKey)), [storageKey]);
  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    try {
      if (next) window.localStorage.setItem(storageKey, "1");
      else window.localStorage.removeItem(storageKey);
    } catch {
      /* The current UI still toggles when storage is blocked. */
    }
  }
  const rows = groups.flatMap((group) => group.rows);
  const thumbnail = (row: LibraryNavigationRow) =>
    row.thumbnail ?? (
      <span className="flex h-full w-full items-center justify-center rounded-md bg-primary/10 text-primary/70">
        <Folder className="h-3.5 w-3.5" aria-hidden />
      </span>
    );
  return (
    <aside
      aria-label={title}
      className={cn(
        "flex shrink-0 flex-col gap-1",
        collapsed ? "w-[52px] items-center" : "w-[248px]",
        className,
      )}
      data-testid={id}
      data-collapsed={String(collapsed)}
    >
      <div
        className={cn(
          "flex h-12 items-center gap-2",
          !collapsed && "w-full justify-between",
        )}
      >
        {!collapsed && (
          <Heading level={2} size="xl" className="font-display font-normal">
            {title}
          </Heading>
        )}
        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 shrink-0 rounded-xl bg-background"
          onClick={toggle}
          aria-label={collapsed ? expandLabel : collapseLabel}
          aria-expanded={!collapsed}
          title={collapsed ? expandLabel : collapseLabel}
          data-testid={`${id}-${collapsed ? "expand" : "collapse"}`}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" strokeWidth={1.8} />
          ) : (
            <PanelLeftClose className="h-4 w-4" strokeWidth={1.8} />
          )}
        </Button>
      </div>
      <button
        type="button"
        onClick={onSelectAll}
        aria-label={allLabel}
        title={collapsed ? allLabel : undefined}
        aria-current={isAllActive ? "page" : undefined}
        className={cn(
          "rounded-[10px] text-left text-sm text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          collapsed
            ? "mt-2 flex h-10 w-10 items-center justify-center border-2 bg-background"
            : "-mx-3 mt-2 px-3 py-2",
          isAllActive
            ? collapsed
              ? "border-primary"
              : "bg-primary/5"
            : collapsed
              ? "border-transparent"
              : "hover:bg-primary/5",
        )}
        data-testid={`${id}-all${collapsed ? "-collapsed" : ""}`}
      >
        {collapsed ? <LayoutGrid className="h-4 w-4" /> : allLabel}
      </button>
      {!collapsed && filters}
      {isLoading ? (
        <div
          className="flex w-full flex-col gap-2 pt-3"
          data-testid={`${id}-loading`}
        >
          {[0, 1, 2].map((position) => (
            <Skeleton key={position} className="h-8 rounded-lg" />
          ))}
        </div>
      ) : hasError ? (
        <Text
          size="sm"
          color="muted"
          className="pt-3"
          data-testid={`${id}-error`}
        >
          {errorLabel}
        </Text>
      ) : collapsed ? (
        rows.map((row, index) => (
          <button
            key={row.token}
            type="button"
            aria-label={row.name}
            title={row.name}
            aria-current={activeToken === row.token ? "page" : undefined}
            onClick={() => onSelect(row.token)}
            className={cn(
              "mt-1 h-10 w-10 overflow-hidden rounded-[10px] border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              activeToken === row.token
                ? "border-primary"
                : "border-transparent",
            )}
            data-testid={`${id}-thumb-${index}`}
          >
            {thumbnail(row)}
          </button>
        ))
      ) : (
        groups.map((group) => (
          <div
            key={group.key}
            className="flex flex-col gap-0.5"
            data-testid={`${id}-group-${group.key}`}
          >
            <Text size="xs" color="muted" className="pb-1 pt-3.5">
              {group.label}
            </Text>
            {group.rows.map((row) => (
              <button
                key={row.token}
                type="button"
                onClick={() => onSelect(row.token)}
                aria-current={activeToken === row.token ? "page" : undefined}
                className={cn(
                  "-mx-3 flex min-w-0 items-center gap-2.5 rounded-[10px] px-3 py-2 text-left text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  activeToken === row.token
                    ? "bg-primary/5"
                    : "hover:bg-primary/5",
                )}
                data-testid={row.testId}
              >
                <span className="h-7 w-7 shrink-0 overflow-hidden rounded-md">
                  {thumbnail(row)}
                </span>
                <Text size="sm" className="min-w-0 flex-1 truncate font-normal">
                  {row.name}
                </Text>
                {row.decoration}
                {row.count !== undefined && (
                  <Text
                    size="xs"
                    color="muted"
                    className="shrink-0 tabular-nums"
                  >
                    {row.count}
                  </Text>
                )}
              </button>
            ))}
          </div>
        ))
      )}
      <Button
        variant="outline"
        onClick={onNew}
        disabled={newDisabled}
        title={newHint}
        aria-label={newLabel}
        className={cn(
          "mt-3 gap-1.5 border-dashed text-primary",
          collapsed
            ? "h-10 w-10 rounded-[10px] p-0"
            : "h-[38px] rounded-[10px] text-sm",
        )}
        data-testid={`${id}-new${collapsed ? "-collapsed" : ""}`}
      >
        <Plus className="h-4 w-4" />
        {!collapsed && newLabel}
      </Button>
      {!collapsed && newHint && (
        <Text size="xs" color="muted" className="pt-2">
          {newHint}
        </Text>
      )}
    </aside>
  );
}
